import type { LiveDeparturesMapState } from '../../../../data/useLiveDeparturesMap';
import { ExploreMap } from '../ExploreMap/ExploreMap';
import { StateMessage } from '../../../base/StateMessage/StateMessage';
import { ResultsPage } from '../../../widget/Pages/Pages';
import type { PlaceOption } from '../../../../data/places';
import { StopSearchForm } from '../StopSearchForm/StopSearchForm';

interface DeparturesResultsPageProps {
  locationQuery: string;
  setLocationQuery: (value: string) => void;
  locationOptions: PlaceOption[];
  onSubmit: (event?: SubmitEvent, selection?: PlaceOption) => void;
  onUseCurrentLocation: () => void | Promise<void>;
  mapState: LiveDeparturesMapState;
  onShowDetails: (detailsId: string) => void;
}

/**
 * Departures results page with map exploration and nearby stop feedback states.
 */
export function DeparturesResultsPage({
  locationQuery,
  setLocationQuery,
  locationOptions,
  onSubmit,
  onUseCurrentLocation,
  mapState,
  onShowDetails,
}: DeparturesResultsPageProps) {
  const {
    selectedLocation,
    onViewportChange,
    canSearchStops,
    nearbyStops,
    nearbyStopsLoading,
    nearbyStopsError,
  } = mapState;

  return (
    <ResultsPage padding="flush">
      <StopSearchForm
        value={locationQuery}
        options={locationOptions}
        onValueChange={setLocationQuery}
        onSubmit={onSubmit}
        onUseCurrentLocation={onUseCurrentLocation}
        autoSubmitOnSelect
        showSubmitButton={false}
        minimal
      />

      {!selectedLocation && (
        <StateMessage
          state="error"
          title="Location unavailable"
          message="Choose a location to explore nearby stops."
        />
      )}

      {selectedLocation && (
        <ExploreMap
          key={selectedLocation.id}
          location={selectedLocation}
          stops={nearbyStops}
          onViewportChange={onViewportChange}
          onSelectStop={onShowDetails}
          aria-label="Explore nearby stops"
        />
      )}

      {canSearchStops && !nearbyStopsLoading && nearbyStopsError && (
        <StateMessage
          state="error"
          title="Could not load stops"
          message={nearbyStopsError.message}
        />
      )}
    </ResultsPage>
  );
}
